/**
 * Which credential flows admin-api can run the OAuth dance for today.
 *
 * One question decides both the routing and the credential payload shape: **is this credential
 * being created through a vendor auth template, three-legged, over OAuth2?** `OauthFlowService`
 * builds its entire parameter universe from `auth_template.auth_parameters`, so anything without
 * a template — or anything it cannot speak — has to stay on the legacy client-side dance.
 *
 * Deliberately excluded for now:
 *  - **OAuth1 / OAuth1a** (any connector). The service implements OAuth2 only.
 *  - **Generic REST** (`oauth2.generic_rest.*`, `rest.template_type`). The user supplies the
 *    endpoints and client credentials on the form; there is no vendor auth template to anchor
 *    them, and `form_values` can only fill parameters a template already declares. Enabling this
 *    needs a templateless mode in `OauthFlowService`, plus a name mapping — the REST spec uses
 *    `oauth2.auth.url` / `oauth2.access.token.url` / `oauth2.client.id` where the API expects
 *    `oauth2.auth_url` / `oauth2.token_url` / `oauth2.client_id`.
 */

const TEMPLATIZED_CATEGORIES = new Set(['templatized_api', 'templatized_api_multi']);

/**
 * Which mode of each migrated core connector is three-legged. The equivalent of the SDK's
 * declarative `auth.oauthMode`; every entry verified against the connector's own
 * `*.fetch_token_button` `dependsOn` in `src/mocks/connectors/**`.
 *
 * `true` means the connector is OAuth-only. box and dropbox gate their button on
 * `vendor.access_token === ''`, which hides it once authorised — a "done yet?" check, not a
 * mode switch.
 */
const CORE_OAUTH_MODE_GATE: Record<string, true | { field: string; oauthValue: string }> = {
  gdrive: { field: 'is_service_account', oauthValue: 'false' },
  gcs: { field: 'is_service_account', oauthValue: 'false' },
  bigquery: { field: 'is_service_account', oauthValue: 'false' },
  google_pubsub: { field: 'is_service_account', oauthValue: 'false' },
  sharepoint: { field: 'oauth2.token_exchange_type', oauthValue: '3-legged' },
  box: true,
  dropbox: true,
};

/**
 * Is a migrated core connector in its three-legged mode, given the values on the form?
 *
 * Needed at **save** time, where — unlike a button click — nothing implies the mode. gdrive in
 * service-account mode must keep writing the core `credentials` payload; in OAuth mode it needs
 * the templatized one, because the migration gave these connectors auth templates.
 */
export function isCoreConnectorInOAuthMode(
  connectionType?: string,
  values?: Record<string, unknown>,
): boolean {
  const gate = connectionType === undefined ? undefined : CORE_OAUTH_MODE_GATE[connectionType];
  if (gate === undefined) return false;
  if (gate === true) return true;
  return String(values?.[gate.field] ?? '') === gate.oauthValue;
}

/** The authTypes `OauthFlowService` can actually negotiate. */
const SERVER_FLOW_AUTH_TYPES = new Set(['oauth2', 'oauth2_generic_rest']);

/** Anything OAuth1-family stays in the browser, whatever else is true about the connector. */
const isOAuth1AuthType = (authType?: string): boolean =>
  authType !== undefined && /oauth1/i.test(authType);

/**
 * `config.authType` is either a string that applies connector-wide (sf_api, gmail) or an object
 * keyed by auth-template name (hub_api, which mixes oauth2 templates with a non-OAuth one).
 */
export function resolveTemplateAuthType(
  rawAuthType: unknown,
  selectedTemplateName?: string | null,
): string | undefined {
  if (typeof rawAuthType === 'string') return rawAuthType;
  if (rawAuthType !== null && typeof rawAuthType === 'object' && selectedTemplateName != null) {
    return (rawAuthType as Record<string, string>)[selectedTemplateName];
  }
  return undefined;
}

interface AuthField {
  name?: string;
  isHiddenField?: boolean;
}

export interface ServerOAuthConnectorArgs {
  connectionCategory?: string;
  /**
   * Does this connector declare an OAuth trigger button? See `hasOAuthTriggerField`. For a
   * non-templatized connector this is what marks it OAuth-capable.
   */
  hasOAuthTrigger?: boolean;
  /** Already resolved for the selected template — see `resolveTemplateAuthType`. */
  authType?: string;
}

/**
 * A core connector is OAuth-capable exactly when its spec declares a `*.fetch_token_button`.
 *
 * Read from the spec rather than kept as a list of connector names. The two agree exactly today
 * — every connector carrying `auth.fetchAuthParams: true` declares one, and vice versa — and
 * when a connector gains server-side OAuth its database records are created as the spec is
 * applied, so there is no window in which the spec claims a capability the backend lacks.
 *
 * `rest` also declares trigger buttons (`oauth1.generic_rest.*`, `oauth2.generic_rest.*`), but
 * `isServerOAuthButton` rejects those before this is consulted.
 */
export function hasOAuthTriggerField(fields?: AuthField[]): boolean {
  return (fields ?? []).some((f) => f?.name?.endsWith('.fetch_token_button') === true);
}

/**
 * Connector-level eligibility.
 *
 * For the migrated core connectors this does not itself check that the form is in OAuth mode —
 * it does not need to. Their OAuth trigger button only renders when its `dependsOn` is satisfied
 * (`is_service_account === 'false'`, `oauth2.token_exchange_type === '3-legged'`), so both
 * callers — a button click, or a form that renders an OAuth2 trigger — already imply OAuth mode.
 */
export function isServerOAuthConnector({
  connectionCategory,
  hasOAuthTrigger,
  authType,
}: ServerOAuthConnectorArgs): boolean {
  // Belt and braces: an OAuth1 authType is disqualifying on any connector, including a migrated
  // core one whose trigger button name carries no hint of it (`sharepoint.fetch_token_button`).
  if (isOAuth1AuthType(authType)) return false;

  if (connectionCategory !== undefined && TEMPLATIZED_CATEGORIES.has(connectionCategory)) {
    return authType !== undefined && SERVER_FLOW_AUTH_TYPES.has(authType);
  }
  return hasOAuthTrigger === true;
}

/**
 * Matches any client-id parameter, whatever namespace it sits in: `oauth2.client_id`,
 * `oauth2.auth_url_param.client_id`, `oauth2.client.id` (REST convention), and the
 * vendor-prefixed form templatized connectors use — `hub_api.client_id`.
 */
const CLIENT_ID_PATTERN = /(^|\.)client[._]id$/i;

/**
 * Does the **user** own the OAuth app, rather than Nexla?
 *
 * Only then is a redirect URL worth showing: it is something they have to register in their own
 * OAuth app. When Nexla owns the app the client id arrives via `param_ref` or a template default
 * and is force-hidden (`reducers/paging/index.js:894`), so there is nothing for them to
 * configure and the banner is just noise.
 *
 * Presence of the field is not the signal — admin-api deliberately keeps client id/secret in the
 * response for templatized vendors even when stripping the rest. Visibility is the signal.
 */
export function userSuppliesOAuthApp(fields?: AuthField[]): boolean {
  return (fields ?? []).some(
    (f) => f?.name !== undefined && CLIENT_ID_PATTERN.test(f.name) && f.isHiddenField !== true,
  );
}

/**
 * Button-level eligibility: does *this* authorize control start a flow the API can run?
 * Keeps OAuth1/1a and the generic-REST controls on the legacy path regardless of connector.
 */
export function isServerOAuthButton(name?: string, value?: unknown): boolean {
  const identity = `${name ?? ''} ${typeof value === 'string' ? value : ''}`;
  if (/oauth1/i.test(identity)) return false;
  if (/generic_rest/i.test(identity)) return false;
  // The REST connector's own flavour dropdown (`OAuth 2 API`, `OAuth 1a API`, …).
  if (value === 'rest.template_type') return false;
  return true;
}
